import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { LuClipboardPaste } from "react-icons/lu";
import { appendCapperNodeToPipeline, editCapperNode, getGlobalParams } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './CapperConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const UNSPECIFIED_COLUMN = 'Column name not specified';

const normalizeOptionalColumn = (value, sentinelLabel = null) => {
  if (value === undefined || value === null) {
    return '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }
  if (sentinelLabel && trimmed.toLowerCase() === sentinelLabel.toLowerCase()) {
    return '';
  }
  return String(value);
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999, // Higher z-index to ensure it appears on top
  }
};

const CapperConfigModal = ({ isOpen, onClose, onConfigured, isReconfiguration = false, initialData = {} }) => {
  // Simple state initialization
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [acdcRatio, setAcdcRatio] = useState(initialData.acdcRatio || '1.2');
  const [colName, setColName] = useState(normalizeOptionalColumn(initialData.colName, UNSPECIFIED_COLUMN));
  const { session } = usePipeline(); // Use the context to get the session
  // New params for capper
  const [isKA, setIsKA] = useState(initialData.KA ?? false);
  const [isMH, setIsMH] = useState(initialData.MH ?? true);
  const [genToTransLoss, setGenToTransLoss] = useState(initialData.genToTransLoss ?? 0);
  // Optional manual override for cap limit (kW). Empty => use calculated
  const [manualCapLimit, setManualCapLimit] = useState(
    (initialData.capLimit === null || initialData.capLimit === undefined) ? '' : String(initialData.capLimit)
  );
  // Live cap limit display
  const [capLimit, setCapLimit] = useState(null);
  const [globalCounts, setGlobalCounts] = useState({ gw: 0, gs: 0, btmFlag: false, mwPerTurbine: 2.1 });

  // Enforce mutual exclusivity between KA and MH
  const onToggleKA = (checked) => {
    if (checked) {
      setIsKA(true);
      setIsMH(false);
    } else {
      // Prevent both being false; keep at least one selected
      if (!isMH) {
        setIsMH(true);
      }
      setIsKA(false);
    }
  };
  const onToggleMH = (checked) => {
    if (checked) {
      setIsMH(true);
      setIsKA(false);
    } else {
      if (!isKA) {
        setIsKA(true);
      }
      setIsMH(false);
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true but not reconfiguring)
  const isEditing = !!initialData.name && initialData.isConfigured === true && !isReconfiguration;
  
  // Keep track of whether the modal has been opened
  const [hasInitialized, setHasInitialized] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Update state only when the modal is opened or initialData changes before user interaction
  useEffect(() => {
    if (isOpen) {
      // Only initialize values if this is the first time opening or if the modal was closed and reopened
      if (!hasInitialized) {
        setNodeName(initialData.name || '');
        setAcdcRatio(initialData.acdcRatio || '1.2');
        setColName(normalizeOptionalColumn(initialData.colName, UNSPECIFIED_COLUMN));
        setIsKA(initialData.KA ?? false);
        setIsMH(initialData.MH ?? true);
        setGenToTransLoss(initialData.genToTransLoss ?? 0);
        setManualCapLimit((initialData.capLimit === null || initialData.capLimit === undefined) ? '' : String(initialData.capLimit));
        
        setHasInitialized(true);
        
        console.log('Modal opened with data:', {
          initialData,
          isReconfiguration: initialData.isReconfiguring || false,
          isEditing: initialData.isEditing || false
        });
      }
    } else {
      // Reset the initialization flag when the modal is closed
      setHasInitialized(false);
    }
  }, [isOpen, initialData, hasInitialized]);

  // Fetch global parameters used for cap calculation (counts of wind turbines and solar units)
  const fetchGlobals = useCallback(async () => {
    try {
      if (!isOpen || !session?.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') return;
      const gp = await getGlobalParams(session.pipeline_name);
      const globals = gp || {};

      const coerceNum = (val) => {
        try {
          if (val === null || val === undefined || val === '') return 0;
          if (typeof val === 'number') return isFinite(val) ? val : 0;
          if (typeof val === 'string') {
            const n = parseFloat(val);
            return isFinite(n) ? n : 0;
          }
          if (typeof val === 'object') {
            const dv = val.__default__;
            if (dv !== undefined && dv !== null && dv !== '') {
              const n = parseFloat(dv);
              if (isFinite(n)) return n;
            }
            const by = val.__by_node__;
            if (by && typeof by === 'object') {
              const first = Object.values(by)[0];
              const n = parseFloat(first);
              if (isFinite(n)) return n;
            }
          }
        } catch (e) {
          return 0;
        }
        return 0;
      };

      // Prefer scaler-driven globals used by backend capper logic.
      // no_of_* may exist from other nodes and can become stale for this helper.
      const gwRaw = (globals.mult_factor_wind ?? globals.no_of_wind_turbines ?? 0);
      const gsRaw = (globals.mult_factor_solar ?? globals.no_of_solar_units ?? 0);
      const gw = coerceNum(gwRaw);
      const gs = coerceNum(gsRaw);
      const btmFlag = (globals.Btm_flag ?? false);
      const mwPerTurbine = coerceNum(globals.mw_per_turbine ?? 2.1) || 2.1;
      setGlobalCounts({ gw, gs, btmFlag, mwPerTurbine });
    } catch (e) {
      // Keep defaults (0) if globals cannot be fetched; avoid blocking UI
      setGlobalCounts({ gw: 0, gs: 0, btmFlag: false, mwPerTurbine: 2.1 });
    }
  }, [isOpen, session?.pipeline_name]);

  useEffect(() => {
    fetchGlobals();
  }, [fetchGlobals]);

  // Live-refresh global counts when scaler/global values are changed elsewhere in the UI.
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;
    const onGlobalsUpdated = (event) => {
      const updatedPipeline = event?.detail?.pipelineName;
      if (!updatedPipeline || updatedPipeline === session?.pipeline_name) {
        fetchGlobals();
      }
    };
    window.addEventListener('pipeline:global-params-updated', onGlobalsUpdated);
    return () => {
      window.removeEventListener('pipeline:global-params-updated', onGlobalsUpdated);
    };
  }, [isOpen, session?.pipeline_name, fetchGlobals]);

  // Helper to compute Cap Limit (kW) mirroring backend logic
  const computeCapLimit = useCallback((gw, gs, acdc, kaFlag, mhFlag, loss, mwPerTurbine) => {
    const mwp_wind = gw * (mwPerTurbine || 2.1); // MWp
    const mwp_solar = gs;      // MWp
    const btmFlag = globalCounts.btmFlag;
    const ratio = !acdc || Number.isNaN(acdc) || acdc === 0 ? 1.0 : acdc;
    const lossFactor = 1 + (Number.isFinite(loss) ? loss : 0);
    let cap = 0;
    if (mhFlag) {
      cap = (mwp_wind + (mwp_solar / ratio)) * 1000 * lossFactor; // kW
    } else if (kaFlag) {
      cap = Math.max(mwp_wind, (mwp_solar / ratio)) * 1000 * lossFactor; // kW
    } 
     else {
      // Default to MH behavior if none selected
      cap = (mwp_wind + (mwp_solar / ratio)) * 1000 * lossFactor;
    }
    if (btmFlag) {
      cap = (mwp_wind * 1000 * lossFactor);
    }
    return cap;
  }, [globalCounts.btmFlag]);

  // Recompute cap limit whenever inputs or globals change
  useEffect(() => {
    try {
      const acdcNum = typeof acdcRatio === 'string' ? parseFloat(acdcRatio) : acdcRatio;
      const lossNum = typeof genToTransLoss === 'string' ? parseFloat(genToTransLoss) : genToTransLoss;
      const cap = computeCapLimit(globalCounts.gw, globalCounts.gs, acdcNum, !!isKA, !!isMH, lossNum || 0, globalCounts.mwPerTurbine);
      setCapLimit(Number.isFinite(cap) ? cap : 0);
    } catch (e) {
      setCapLimit(null);
    }
  }, [acdcRatio, isKA, isMH, genToTransLoss, globalCounts.gw, globalCounts.gs, globalCounts.btmFlag, globalCounts.mwPerTurbine, computeCapLimit]);
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);
  
  // Function to paste from clipboard
  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setColName(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      // Make sure we have a valid AC/DC ratio
      if (!acdcRatio || isNaN(acdcRatio)) {
        setSubmitError({
          title: 'Invalid AC/DC ratio',
          message: 'Please enter a valid AC/DC ratio (numeric value).',
        });
        return;
      }

      // Column name is optional now; leaving blank auto-selects last column
      
      // Get the input node name and position from initialData
      const inputNodeName = initialData.inputNodeName;
      const position = initialData.position || { x: 0, y: 0 };
      const normalizedColName = normalizeOptionalColumn(colName, UNSPECIFIED_COLUMN);
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // We're editing if isEditing is true (which means the node has a name AND is already configured)
      
      console.log("Configuration mode:", {
        isEditing,
        isReconfiguration,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          // Parse manual cap limit (empty/invalid => null)
          let capLimitToSend = null;
          if (manualCapLimit !== null && manualCapLimit !== undefined && String(manualCapLimit).trim() !== '') {
            const n = parseFloat(manualCapLimit);
            capLimitToSend = Number.isFinite(n) ? n : null;
          }

          if (isEditing) {
            // We're editing an existing node
            console.log(`Calling API to edit capper node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New AC/DC ratio: ${acdcRatio}, New column name: ${normalizedColName}`);
            
            // In edit mode, we only update the AC/DC ratio and column name
            await editCapperNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              acdcRatio,
              normalizedColName || null,
              { KA: isKA, MH: isMH, genToTransLoss, capLimit: capLimitToSend }
            );
            
            console.log(`Successfully edited capper node ${initialData.name}`);
          } else if (!isReconfiguration && inputNodeName) {
            // We're creating a new node
            console.log(`Calling API to append capper node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Input node: ${inputNodeName}, AC/DC Ratio: ${acdcRatio}, Column: ${normalizedColName}, Position: (${position.x}, ${position.y})`);
            
            await appendCapperNodeToPipeline(
              session.pipeline_name,
              nodeName,
              acdcRatio,
              normalizedColName || "",
              inputNodeName,
              position.x,
              position.y,
              { KA: isKA, MH: isMH, genToTransLoss, capLimit: capLimitToSend }
            );
            
            console.log(`Successfully added capper node ${nodeName} to backend`);
          } else if (isReconfiguration) {
            console.log(`Skipping API call for node ${nodeName} as it's being reconfigured by parent component`);
          }
        } catch (apiError) {
          console.error(`Error calling ${isEditing ? 'editCapperNode' : 'appendCapperNodeToPipeline'}:`, apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editCapper : OP.node.appendCapper,
            showToast: false,
          });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        acdcRatio: acdcRatio,
        colName: normalizedColName,
        KA: isKA,
        MH: isMH,
        genToTransLoss: genToTransLoss,
        capLimit: (manualCapLimit !== null && manualCapLimit !== undefined && String(manualCapLimit).trim() !== '')
          ? (Number.isFinite(parseFloat(manualCapLimit)) ? parseFloat(manualCapLimit) : null)
          : null
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring capper node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
      return; // Exit early on error
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Capper Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="capper-modal">
        <button onClick={onClose} className="capper-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="capper-modal-title">
          {isReconfiguration ? "Reconfigure Capper Node" : 
           isEditing ? "Edit Capper Parameters" : 
           "Configure New Capper Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="capper-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="capper-modal-field">
          <label className="capper-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="capper-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`capper-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="capper-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="capper-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div className="capper-modal-field">
          <label className="capper-modal-label">AC/DC Ratio:</label>
          <input
            type="number"
            value={acdcRatio}
            onChange={(e) => setAcdcRatio(e.target.value)}
            className="capper-modal-input"
            placeholder="Enter AC/DC ratio (e.g. 1.2)"
            step="1"
          />
        </div>

        <div className="capper-modal-field">
          <label className="capper-modal-label">Region Flags:</label>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="checkbox" checked={!!isKA} onChange={(e) => onToggleKA(e.target.checked)} />
              KA (Karnataka)
            </label>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="checkbox" checked={!!isMH} onChange={(e) => onToggleMH(e.target.checked)} />
              MH (Maharashtra)
            </label>
          </div>
        </div>

        <div className="capper-modal-field">
          <label className="capper-modal-label">Generation to Transmission Loss (fraction):</label>
          <input
            type="number"
            value={genToTransLoss}
            onChange={(e) => setGenToTransLoss(e.target.value)}
            className="capper-modal-input"
            placeholder="e.g. 0.0"
            step="0.01"
          />
        </div>

        <div className="capper-modal-field">
          <label className="capper-modal-label">Calculated Cap Limit (kW):</label>
          <div className="capper-modal-value" title="Derived from global wind/solar counts, AC/DC ratio, region, and loss">
            {capLimit === null ? '—' : (Math.round((capLimit + Number.EPSILON) * 100) / 100).toLocaleString()}
          </div>
        </div>

        <div className="capper-modal-field">
          <label className="capper-modal-label">Manual Cap Limit Override (kW):</label>
          <input
            type="number"
            value={manualCapLimit}
            onChange={(e) => setManualCapLimit(e.target.value)}
            className="capper-modal-input"
            placeholder="Leave blank to use calculated cap"
            step="1"
          />
          <div className="capper-modal-hint">
            Leave blank to use the calculated value above. If set, this override will be used during pipeline runs.
          </div>
        </div>

        <div className="capper-modal-field">
          <label className="capper-modal-label">
            Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={colName}
              onChange={(e) => setColName(e.target.value)}
              className="capper-modal-input"
              placeholder="Leave blank to auto-select last output column"
              title="Optional: leave blank to auto-select the last output column from the previous node"
            />
            <button 
              type="button"
              onClick={pasteFromClipboard}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>
        
        <div className="capper-modal-button-container">
          <button 
            className="capper-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName)) || 
              !acdcRatio || isNaN(acdcRatio)
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : 
              !acdcRatio || isNaN(acdcRatio) ? 'Valid AC/DC ratio is required' : ''
            }
          >
            {isSubmitting ? 'Processing...' : 
              isReconfiguration ? 'Reconfigure' : 
              isEditing ? 'Save Changes' : 
              'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CapperConfigModal;
